import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

import { observationTypeSchema } from "./observationtypes";

export const timeseriesListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  start: lizardDateSchema.nullable(),
  end: lizardDateSchema.nullable(),
  value_type: z.string(),
  last_value: z.string().or(z.number()).nullable(),
  interval: z.number().nullable(),
  observation_type: observationTypeSchema.nullable(),
  datasource: z.string().nullable(),
  supplier: z.string().nullable(),
  supplier_code: z.string().nullable(),
  location: z.object({
    url: z.string(),
    uuid: z.string(),
    name: z.string(),
    code: z.string(),
  }),
  extra_metadata: z.record(z.any()),
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
  access_modifier: z.string(),
});

export type TimeseriesListItem = z.infer<typeof timeseriesListItemSchema>;

export const timeseriesListSchema = paginatedSchema(timeseriesListItemSchema);
export type TimeseriesList = z.infer<typeof timeseriesListSchema>;

export const timeseriesSchema = timeseriesListItemSchema;
export type Timeseries = z.infer<typeof timeseriesSchema>;

export function useTimeseries(params = {}) {
  const url = combineUrlAndParams("/api/v4/timeseries/", params);
  return useParsedQuery<TimeseriesList>(
    ["lizard-api-v4", "timeseries", url],
    () => fetchWithError(url),
    (json: unknown) => timeseriesListSchema.parse(json)
  );
}

export function useSingleTimeseries(uuid: string) {
  return useParsedQuery<Timeseries>(
    ["lizard-api-v4", "timeseries", uuid],
    () => fetchWithError(`/api/v4/timeseries/${uuid}/`),
    (json: unknown) => timeseriesSchema.parse(json),
    { enabled: uuid !== "" }
  );
}

export function setSingleTimeseriesCache(
  queryClient: QueryClient,
  uuid: string,
  timeseries: Timeseries
) {
  queryClient.setQueryData(["lizard-api-v4", "timeseries", uuid], timeseries);
}

// Use this outside components, so the fetched timeseries can still
// be stored in cache.
export async function fetchAndCacheTimeseries(
  queryClient: QueryClient,
  uuid: string
): Promise<Timeseries | null> {
  try {
    const response = await fetchWithError(`/api/v4/timeseries/${uuid}/`);
    const timeseries = timeseriesSchema.parse(response);
    setSingleTimeseriesCache(queryClient, uuid, timeseries);
    return timeseries;
  } catch (e) {
    return null;
  }
}

export function useMultipleTimeseries(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "timeseries", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/timeseries/${uuid}/`),
        parseFunction: (json: unknown) => timeseriesSchema.parse(json),
      };
    })
  );
}

// Helper functions for timeseries
export function timeseriesHasUniqueLocationName(timeseries: Timeseries[]) {
  const uniqueLocationNames = Array.from(
    new Set(
      timeseries.map((ts) => {
        return ts.location.name;
      })
    )
  );
  return uniqueLocationNames.length > 1;
}

export function getTimeseriesChartTitle(
  timeseries: Timeseries[],
  ts: Timeseries,
  includeLocationName?: boolean
) {
  const hasUniqueLocationName = timeseriesHasUniqueLocationName(timeseries);
  const timeseriesName = ts.observation_type ? ts.observation_type.parameter : ts.name;

  return includeLocationName || hasUniqueLocationName
    ? `${ts.location.name} - ${timeseriesName}`
    : timeseriesName;
}
